import { createRng } from './rng';

/**
 * TickStar partner-bridge fixtures for the Solara admin/customer demos.
 *
 * Active partner-bridge fixtures (see [[solara-tickstar-swap]]
 * memo + 2026-05-14 commercial decision). The shape mirrors what the
 * **MockTickstarProvider** in `@solara/peppol-tickstar/mock` produces, so the
 * admin /tickstar dashboards stay coherent with the underlying API contract.
 *
 * Everything here is seeded — screenshots and screen recordings stay stable
 * between reloads.
 */

// --- API capability matrix -----------------------------------------

export type TickstarCapability =
  | 'transaction-send'
  | 'transaction-receive'
  | 'transaction-status'
  | 'transaction-acknowledge'
  | 'participant-lookup'
  | 'smp-register'
  | 'smp-activate'
  | 'webhook-delivery';

export interface TickstarApiEndpoint {
  capability: TickstarCapability;
  /** Human-friendly label for the admin matrix. */
  label: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  /** TickStar Transaction API / SMP Manager API path. */
  path: string;
  /** Last measured p95 latency over 7 days, ms. */
  p95Ms: number;
  /** Rolling 7-day success rate (0..1). */
  successRate: number;
  /** TickStar SLA target, ms (derived from Appendix B of SaaS Agreement). */
  slaP95Ms: number;
  /** TickStar API version reported by the gateway. */
  partnerVersion: string;
  status: 'healthy' | 'degraded' | 'failing';
  description: string;
}

export function generateTickstarApiMatrix(seed = 0x7c_5a_03): TickstarApiEndpoint[] {
  const rng = createRng(seed);
  const items: Omit<TickstarApiEndpoint, 'p95Ms' | 'successRate' | 'status'>[] = [
    {
      capability: 'transaction-send',
      label: 'Peppol AS4 send (C2 → C3)',
      method: 'POST',
      path: '/transaction-api/1.0/transactions?networkType=PEPPOL',
      slaP95Ms: 5000,
      partnerVersion: '2026.05.0',
      description:
        'SBDH + UBL payload submitted to TickStar; returns transactionId + messageIdentifier.',
    },
    {
      capability: 'transaction-receive',
      label: 'List INBOUND transactions',
      method: 'GET',
      path: '/transaction-api/1.0/transactions?direction=INBOUND',
      slaP95Ms: 1500,
      partnerVersion: '2026.05.0',
      description: 'Polling fallback when webhooks are unavailable; filterable by messageIdentifier.',
    },
    {
      capability: 'transaction-status',
      label: 'GET transaction by ID',
      method: 'GET',
      path: '/transaction-api/1.0/transactions/:id',
      slaP95Ms: 800,
      partnerVersion: '2026.05.0',
      description: 'State machine: PROCESSING → COMPLETED → ACKNOWLEDGED.',
    },
    {
      capability: 'transaction-acknowledge',
      label: 'PUT acknowledge transaction',
      method: 'PUT',
      path: '/transaction-api/1.0/transactions/:id/acknowledge',
      slaP95Ms: 700,
      partnerVersion: '2026.05.0',
      description: 'Acknowledges the transaction; releases it for the 90-day TickStar retention purge.',
    },
    {
      capability: 'participant-lookup',
      label: 'Participant Lookup',
      method: 'GET',
      path: '/1.0/smp/smplookup.json?pid=…',
      slaP95Ms: 1200,
      partnerVersion: '2026.05.0',
      description:
        'Resolves a Peppol ID against the SML (production) or SMK (test) registry. 50 000 lookups/month included.',
    },
    {
      capability: 'smp-register',
      label: 'SMP register participant',
      method: 'POST',
      path: '/2.1/smp/participant',
      slaP95Ms: 2200,
      partnerVersion: '2026.05.0',
      description: 'Registers a tenant participant in the TickStar SMP; KYC-gated in select regions.',
    },
    {
      capability: 'smp-activate',
      label: 'SMP activate in SML + directory',
      method: 'PUT',
      path: '/2.1/smp/sml/activate/:id',
      slaP95Ms: 1800,
      partnerVersion: '2026.05.0',
      description: 'Activates the participant in the central SML; optional Peppol Directory publish.',
    },
    {
      capability: 'webhook-delivery',
      label: 'Webhook delivery (in)',
      method: 'POST',
      path: 'POST {tenant-webhook-url}',
      slaP95Ms: 4000,
      partnerVersion: '2026.05.0',
      description:
        'TickStar pushes transaction events to the Solara webhook receiver, signed with HMAC.',
    },
  ];
  return items.map((it) => {
    const drift = rng.next();
    const p95 = Math.round(it.slaP95Ms * (0.45 + drift * 0.6));
    const successRate = Number((0.972 + rng.next() * 0.027).toFixed(4));
    const status: TickstarApiEndpoint['status'] =
      successRate < 0.985 || p95 > it.slaP95Ms ? 'degraded' : 'healthy';
    return { ...it, p95Ms: p95, successRate, status };
  });
}

// --- Webhook event log --------------------------------------------

export type TickstarWebhookTopic =
  | 'transaction.processing'
  | 'transaction.completed'
  | 'transaction.acknowledged'
  | 'transaction.failed'
  | 'transaction.quarantined'
  | 'participant.registered'
  | 'participant.activated'
  | 'kyc.required';

export interface TickstarWebhookEvent {
  webhookId: string;
  receivedAt: string;
  topic: TickstarWebhookTopic;
  /** Solara-side delivery state. */
  delivery: 'delivered' | 'retrying' | 'dlq' | 'replayed';
  /** Solara endpoint that accepts the webhook. */
  target: string;
  /** HMAC signature prefix (display only). */
  signature: string;
  transactionId?: string;
  participantId?: string;
  tenantId?: string;
  attempts: number;
  durationMs: number;
  errorCode?: string;
  errorMessage?: string;
}

export function generateTickstarWebhooks(count = 30, seed = 0x9f_b1_dd): TickstarWebhookEvent[] {
  const rng = createRng(seed);
  const TOPICS: TickstarWebhookTopic[] = [
    'transaction.processing',
    'transaction.completed',
    'transaction.acknowledged',
    'transaction.failed',
    'transaction.quarantined',
    'participant.registered',
    'participant.activated',
    'kyc.required',
  ];
  const TARGETS = [
    'POST https://core.solara.ae/webhooks/tickstar/transactions',
    'POST https://core.solara.ae/webhooks/tickstar/participants',
    'POST https://core.solara.ae/webhooks/tickstar/kyc',
  ];
  const ERRORS = [
    { code: 'SIG_MISMATCH',  message: 'HMAC signature mismatch — possible replay' },
    { code: 'TIMEOUT',       message: 'Solara webhook receiver exceeded 5 s read timeout' },
    { code: 'IDEM_CONFLICT', message: 'Idempotency key already processed for different payload' },
    { code: '503',           message: 'Solara /webhooks/tickstar returned 503' },
  ];
  const out: TickstarWebhookEvent[] = [];
  for (let i = 0; i < count; i++) {
    const r = rng.next();
    const delivery: TickstarWebhookEvent['delivery'] =
      r < 0.86 ? 'delivered' : r < 0.94 ? 'retrying' : r < 0.97 ? 'replayed' : 'dlq';
    const topic = rng.pick(TOPICS);
    const at = new Date(Date.now() - rng.int(0, 24 * 3600_000));
    const failed = delivery === 'dlq' || delivery === 'retrying';
    const err = failed ? rng.pick(ERRORS) : undefined;
    out.push({
      webhookId: `wh-tks-${String(rng.int(1, 999_999)).padStart(6, '0')}`,
      receivedAt: at.toISOString(),
      topic,
      delivery,
      target: rng.pick(TARGETS),
      signature: `sha256=${Array.from({ length: 12 }, () => rng.int(0, 15).toString(16)).join('')}…`,
      transactionId: rng.chance(0.85)
        ? `tx-${String(rng.int(10_000, 99_999_999)).padStart(8, '0')}`
        : undefined,
      participantId: rng.chance(0.4)
        ? `iso6523-actorid-upis::0230:UAE-TRN-${rng.int(1000_0000_0000_000, 9_999_999_999_999_999)}`
        : undefined,
      tenantId: rng.chance(0.95)
        ? `tnt-${String(rng.int(100, 9999)).padStart(4, '0')}`
        : undefined,
      attempts: delivery === 'delivered' ? 1 : rng.int(2, 6),
      durationMs: rng.int(40, topic.startsWith('transaction.') ? 4500 : 1800),
      errorCode: err?.code,
      errorMessage: err?.message,
    });
  }
  out.sort((a, b) => (a.receivedAt < b.receivedAt ? 1 : -1));
  return out;
}

// --- TickStar partner SLA cards -----------------------------------

export interface TickstarSlaCard {
  bucket: 'availability' | 'send' | 'receive' | 'smp' | 'lookup' | 'support';
  /** Contractual target — see Appendix B of `(RoW) Tickstar SaaS Agreement`. */
  target: string;
  /** Measured value over the last 30 days. */
  measured: string;
  /** Target metric numeric (for sparkline coloring), 0..1 for ratios or ms for latency. */
  measuredNumeric: number;
  targetNumeric: number;
  kind: 'ratio' | 'latency' | 'duration';
  /** SLA credit accrued this period (EUR). */
  creditsEur: number;
  trend: 'up' | 'down' | 'flat';
}

export function generateTickstarSlaCards(seed = 0x51_a_5_05): TickstarSlaCard[] {
  const rng = createRng(seed);
  return [
    {
      bucket: 'availability',
      target: '99.5% monthly',
      measured: `${(99.4 + rng.next() * 0.6).toFixed(2)}%`,
      measuredNumeric: 0.994 + rng.next() * 0.006,
      targetNumeric: 0.995,
      kind: 'ratio',
      creditsEur: rng.chance(0.15) ? rng.int(150, 950) : 0,
      trend: rng.pick(['up', 'flat']),
    },
    {
      bucket: 'send',
      target: 'p95 < 5 000 ms',
      measured: `${rng.int(2400, 5600)} ms p95`,
      measuredNumeric: rng.int(2400, 5600),
      targetNumeric: 5000,
      kind: 'latency',
      creditsEur: 0,
      trend: rng.pick(['up', 'down', 'flat']),
    },
    {
      bucket: 'receive',
      target: 'p95 < 4 000 ms',
      measured: `${rng.int(1500, 4400)} ms p95`,
      measuredNumeric: rng.int(1500, 4400),
      targetNumeric: 4000,
      kind: 'latency',
      creditsEur: 0,
      trend: rng.pick(['up', 'down', 'flat']),
    },
    {
      bucket: 'smp',
      target: 'p95 < 2 000 ms',
      measured: `${rng.int(900, 2200)} ms p95`,
      measuredNumeric: rng.int(900, 2200),
      targetNumeric: 2000,
      kind: 'latency',
      creditsEur: 0,
      trend: rng.pick(['flat']),
    },
    {
      bucket: 'lookup',
      target: '50 000 / month',
      measured: `${rng.int(8_200, 38_400)} / mo`,
      measuredNumeric: rng.int(8200, 38400),
      targetNumeric: 50_000,
      kind: 'duration',
      creditsEur: 0,
      trend: rng.pick(['up', 'flat']),
    },
    {
      bucket: 'support',
      target: 'Critical 4h ack / 1 BD',
      measured: '3h 12m avg ack',
      measuredNumeric: 3.2 * 3600,
      targetNumeric: 4 * 3600,
      kind: 'duration',
      creditsEur: 0,
      trend: rng.pick(['up', 'flat']),
    },
  ];
}

// --- TickStar usage card (against bundle quotas) ------------------

export interface TickstarUsageSnapshot {
  bundle: 'White-label' | 'Standard';
  tier: 'Starter' | 'Regular' | 'Premium' | 'Custom';
  hostingRegion: 'EU' | 'ANZ' | 'SEA';
  /** Pilot tier — Starter caps. */
  monthlyTransactionCap: number;
  monthlyTransactionsUsed: number;
  participantCap: number;
  participantsRegistered: number;
  lookupCap: number;
  lookupsUsedThisMonth: number;
  /** Days remaining before the cap resets (calendar month). */
  daysUntilReset: number;
  /** Whether the customer is currently in overage. */
  overage: boolean;
  /** Optional EUR cost-to-date this month. */
  costEurMtd: number;
}

export function generateTickstarUsage(seed = 0xa01a_d1_4): TickstarUsageSnapshot {
  const rng = createRng(seed);
  return {
    bundle: 'White-label',
    tier: 'Starter',
    hostingRegion: 'EU',
    monthlyTransactionCap: 5000,
    monthlyTransactionsUsed: rng.int(800, 3200),
    participantCap: 50,
    participantsRegistered: rng.int(8, 34),
    lookupCap: 50_000,
    lookupsUsedThisMonth: rng.int(1200, 22_400),
    daysUntilReset: rng.int(1, 28),
    overage: false,
    costEurMtd: 750,
  };
}

// --- TickStar dead-letter / replay queue --------------------------

export interface TickstarDlqEntry {
  dlqId: string;
  enqueuedAt: string;
  topic: TickstarWebhookTopic;
  reason: string;
  payloadSnippet: string;
  retriedAttempts: number;
  /** Tenant whose webhook the message belongs to. */
  tenantId: string;
  /** Solara owner — who should resolve. */
  owner: 'platform-admin' | 'on-call' | 'tenant-ops';
}

export function generateTickstarDlq(count = 12, seed = 0xd1_c0_99): TickstarDlqEntry[] {
  const rng = createRng(seed);
  const REASONS = [
    'Solara webhook receiver returned 503 three times',
    'HMAC signature mismatch (rotated secret not yet in effect?)',
    'Idempotency-key conflict on /webhooks/tickstar/transactions',
    'Timeout — handler exceeded 5 s SLA',
    'Schema validation failed on inbound INBOUND_SOURCE artefact',
  ];
  const TOPICS: TickstarWebhookTopic[] = [
    'transaction.failed',
    'transaction.quarantined',
    'kyc.required',
    'participant.registered',
  ];
  return Array.from({ length: count }, (_, i) => ({
    dlqId: `dlq-${String(i + 1).padStart(4, '0')}`,
    enqueuedAt: new Date(Date.now() - rng.int(60_000, 14 * 86_400_000)).toISOString(),
    topic: rng.pick(TOPICS),
    reason: rng.pick(REASONS),
    payloadSnippet: `{"transactionId":"tx-${rng.int(10_000, 99_999_999)}","state":"FAILED",...}`,
    retriedAttempts: rng.int(3, 7),
    tenantId: `tnt-${String(rng.int(100, 9999)).padStart(4, '0')}`,
    owner: rng.pick(['platform-admin', 'on-call', 'tenant-ops']),
  }));
}
